import {
  Body,
  Controller,
  Post,
  Res,
  UploadedFile,
  UseInterceptors,
} from "@nestjs/common";
import { ApiBody, ApiTags, ApiConsumes } from "@nestjs/swagger";
import { BomService } from "./bom-service";
import {
  BomExcelreq,
  BomProposalReq,
  CommonResponseModel,
  PpmDateFilterRequest,
  updateItemId,
  StyleNumReq,
  StylesResponseModel,
  BomResponseModel,
  StylesComboResponseModel,
  SizeMatrixFabricCodeDto,
  BomDto,
  ImCodeItemMapReq,
} from "@project-management-system/shared-models";
import { ApplicationExceptionHandler } from "@project-management-system/backend-utils";
import { StyleDto } from "./dto/style-dto";
import { TrimService } from "./trim-service";
import { StyleNumberDto } from "./dto/style-number-dto";
import { StyleIdReq } from "./dto/api-requests";
import { FileInterceptor } from "@nestjs/platform-express";
import { diskStorage } from "multer";
import { StyleReqDto } from "./dto/style-master-req";
import { StyleBomDto } from "./dto/bom-style.dto";
import { BomMasterDto } from "./dto/bom-master.dto";
import { StyleComboMasterDto } from "./dto/style-combo-master.dto";
import { BomFormDto } from "./dto/bom-form-dto";
import { Response } from "express";
import { OrderFormMappingDto } from "./dto/order-form-im-mapping";

@ApiTags("bom")
@Controller("bom")
export class BomController {
  constructor(
    private readonly bomService: BomService,
    private readonly applicationExceptionHandler: ApplicationExceptionHandler,
    private readonly trimService: TrimService
  ) {}

  @Post("/createBom")
  async createBom(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.createBom(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @Post("/getAllStylesData")
  async getAllStylesData(): Promise<CommonResponseModel> {
    try {
      return this.bomService.getAllStylesData();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @Post("/getAll")
  async getAll(): Promise<CommonResponseModel> {
    try {
      return this.bomService.getAll();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getPpmPoLineData")
  async getPpmPoLineData(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getPpmPoLineData(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getAllTrimInfo")
  async getAllTrimInfo(): Promise<CommonResponseModel> {
    try {
      return this.trimService.getAllTrimInfo();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @ApiBody({ type: StyleNumberDto })
  @Post("/getBomInfoAgainstStyle")
  async getBomInfoAgainstStyle(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.trimService.getBomInfoAgainstStyle(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getItemInfo")
  async getItemInfo(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.trimService.getItemInfo(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getItemDropdownByCreatedAt")
  async getItemDropdownByCreatedAt(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.trimService.getItemDropdownByCreatedAt(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getRegionDropdownByCreatedAt")
  async getRegionDropdownByCreatedAt(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.trimService.getRegionDropdownByCreatedAt(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getStylesData")
  @ApiBody({ type: StyleReqDto })
  async getStylesData(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getStylesData(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @Post("/getPoLineDataForCihinaInserttag")
  async getPoLineDataForCihinaInserttag(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.getPoLineDataForCihinaInserttag(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getBomPrintInfo")
  async getBomPrintInfo(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.trimService.getBomPrintInfo(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @ApiBody({ type: StyleIdReq })
  @Post("/getBomDataForStyle")
  async getBomDataForStyle(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getBomDataForStyle(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getBomCreationData")
  async getBomCreationData(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getBomCreationData(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateBom")
  async generateBom(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateBom(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/saveExcelData")
  @UseInterceptors(
    FileInterceptor("file", {
      limits: { files: 1 },
      storage: diskStorage({
        destination: "./upload-files",
        filename: (req, filea, callback) => {
          const name = filea.originalname;
          callback(null, `${name}`);
        },
      }),
    })
  )
  @ApiConsumes("multipart/form-data")
  async saveExcelData(@UploadedFile() file): Promise<any> {
    try {
      return this.bomService.saveExcelData(file);
    } catch (err) {
      console.log(err);
    }
  }

  @Post("/migrateData")
  async migrateData(): Promise<any> {
    try {
      return this.bomService.migrateData();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getbomexcel")
  async getBomExcel(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getBomExcel(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateProposal")
  async generateProposal(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForWashcareLabel(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateProposalForButton")
  @ApiBody({ type: BomProposalReq })
  async generateProposalForButton(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForButton(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getbom")
  @ApiBody({ type: BomExcelreq })
  async getBom(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getBom(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getStyle")
  async getStyle(): Promise<CommonResponseModel> {
    try {
      return this.bomService.getStyle();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getGeoCode")
  async getGeoCode(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getGeoCode();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @Post("/generateProposalForNeckTape")
  @ApiBody({ type: BomProposalReq })
  async generateProposalForNeckTape(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForNeckTape(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @Post("/generateProposalForTrims")
  async generateProposalForTrims(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForTrims(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }
  @Post("/generateProposalForElasticTrim")
  async generateProposalForElasticTrim(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForElasticTrim(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }
  @Post("/generatePropsalForHtLabel")
  async generatePropsalForHtLabel(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generatePropsalForHtLabel(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }
  @Post("/getSizeHtLabelData")
  async getSizeHtLabelData(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getSizeHtLabelData(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }

  @Post("/generateProposalForTissuePaper")
  @ApiBody({ type: BomProposalReq })
  async generateProposalForTissuePaper(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForTissuePaper(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }
  @Post("/getMainWovenLableData")
  @ApiBody({ type: BomProposalReq })
  async getMainWovenLableData(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getMainWovenLableData1(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }

  @Post("/getSizeStrip")
  async getSizeStrip(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getSizeStrip(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }
  @Post("/getProductCodeDropdownByCreatedAt")
  async getProductCodeDropdownByCreatedAt(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.trimService.getProductCodeDropdownByCreatedAt(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getSeasonCodeDropdownByCreatedAt")
  async getSeasonCodeDropdownByCreatedAt(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.trimService.getSeasonCodeDropdownByCreatedAt(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @Post("/getSeasonYearDropdownByCreatedAt")
  async getSeasonYearDropdownByCreatedAt(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.trimService.getSeasonYearDropdownByCreatedAt(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getImcodes")
  async getImcodes(): Promise<CommonResponseModel> {
    try {
      return this.bomService.getImcodes();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @ApiBody({ type: StyleNumReq })
  @Post("/getApaSizeMatrix")
  async getApaSizeMatrix(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getApaSizeMatrix(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getItemname")
  async getItemname(): Promise<CommonResponseModel> {
    try {
      return this.bomService.getItemname();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @Post("/updateItemid")
  async updateItemid(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.updateItemid(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateProposalForPOIDLabel")
  async generateProposalForPOIDLabel(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForPOIDLabel(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }

  @Post("/getPpmStyleNumberByCreatedAt")
  async getPpmStyleNumberByCreatedAt(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.trimService.getPpmStyleNumberByCreatedAt(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }

  @Post("/generateProposalForKimble")
  async generateProposalForKimble(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForKimble(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }

  @Post("/generateProposalForHmSheet")
  async generateProposalForHmSheet(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForHmSheet(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }

  @Post("/getProposalForGumtape")
  @ApiBody({ type: BomProposalReq })
  async getProposalForGumtape(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getProposalForGumtape(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getBackingPaperV2")
  @ApiBody({ type: BomProposalReq })
  async getBackingPaperV2(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getBackingPaperV2(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getAllConsumptionRequiredTrims")
  async getAllConsumptionRequiredTrims(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.trimService.getAllConsumptionRequiredTrims(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getUniqueSizeAgainstStyleAndItem")
  async getUniqueSizeAgainstStyleAndItem(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.getUniqueSizeAgainstStyleAndItem(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getProposalForCartonBox")
  @ApiBody({ type: BomProposalReq })
  async getProposalForCartonBox(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.getProposalForCartonBox(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateGSISticker")
  @ApiBody({ type: BomProposalReq })
  async generateGSISticker(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateGSISticker(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getPatchLabel")
  @ApiBody({ type: BomProposalReq })
  async getPatchLabel(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getPatchLabel(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateProposalForZipperTrim")
  @ApiBody({ type: BomProposalReq })
  async generateProposalForZipperTrim(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForZipperTrim(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getAllStylesdynamic")
  async getAllStylesdynamic(): Promise<CommonResponseModel> {
    try {
      return this.bomService.getAllStylesdynamic();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getConsumptionFromBOM")
  async getConsumptionFromBOM(req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getConsumptionFromBOM(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateProposalForSnapButton")
  @ApiBody({ type: BomProposalReq })
  async generateProposalForSnapButton(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForSnapButton(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateProposalForJocktage")
  @ApiBody({ type: BomProposalReq })
  async generateProposalForJocktage(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForJocktage(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateProposalForCountrySticker")
  @ApiBody({ type: BomProposalReq })
  async generateProposalForCountrySticker(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForCountrySticker(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateProposalForSwoosh")
  @ApiBody({ type: BomProposalReq })
  async generateProposalForSwoosh(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForSwoosh(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateProposalForSpareButtonLabel")
  @ApiBody({ type: BomProposalReq })
  async generateProposalForSpareButtonLabel(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForSpareButtonLabel(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateProposalForMobillionTape")
  @ApiBody({ type: BomProposalReq })
  async generateProposalForMobillionTape(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForMobillionTape(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateProposalForTwilltape")
  @ApiBody({ type: BomProposalReq })
  async generateProposalForTwilltape(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForTwilltape(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @Post("/generateInterlining")
  async generateInterlining(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateInterlining(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }

  @Post("/getStylesAndImsForSelectedItems")
  async getStylesAndImsForSelectedItems(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.getStylesAndImsForSelectedItems(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }
  @Post("/generateForbackingPaper")
  async generateForbackingPaper(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateForbackingPaper(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }

  @Post("/generatePRoposalForSizeHt")
  async generatePRoposalForSizeHt(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generatePRoposalForSizeHt(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }

  @Post("/getBomAndImsForSelectedItems")
  async getBomAndImsForSelectedItems(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.getBomAndImsForSelectedItems(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }
  @Post("/getStylefromBom")
  async getStylefromBom(): Promise<CommonResponseModel> {
    try {
      return this.bomService.getStylefromBom();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @Post("/getSeasonfromBom")
  async getSeasonfromBom(): Promise<CommonResponseModel> {
    try {
      return this.bomService.getSeasonfromBom();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @Post("/getYearfromBom")
  async getYearfromBom(): Promise<CommonResponseModel> {
    try {
      return this.bomService.getYearfromBom();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @ApiBody({ type: StyleBomDto })
  @Post("/updateStyle")
  async updateStyle(@Body() req: any): Promise<StylesResponseModel> {
    try {
      return this.bomService.updateStyle(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        StylesResponseModel,
        err
      );
    }
  }
  @ApiBody({ type: BomMasterDto })
  @Post("/updateBomTable")
  async updateBomTable(@Body() req: any): Promise<BomResponseModel> {
    try {
      return this.bomService.updateBomTable(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        BomResponseModel,
        err
      );
    }
  }
  @ApiBody({ type: StyleComboMasterDto })
  @Post("/updateStyleCombo")
  async updateStyleCombo(@Body() req: any): Promise<StylesComboResponseModel> {
    try {
      return this.bomService.updateStyleCombo(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        StylesComboResponseModel,
        err
      );
    }
  }

  @Post("/getSizeHtMatrixData")
  async getSizeHtMatrixData(): Promise<CommonResponseModel> {
    try {
      return this.bomService.getSizeHtMatrixData();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @Post("/getImcodesFromBOm")
  async getImcodesFromBOm(): Promise<CommonResponseModel> {
    try {
      return this.bomService.getImcodesFromBOm();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateProposalForJokerTag")
  @ApiBody({ type: BomProposalReq })
  async generateProposalForJokerTag(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForJokerTag(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getStylesfromStyleTable")
  async getStylesfromStyleTable(): Promise<CommonResponseModel> {
    try {
      return this.bomService.getStylesfromStyleTable();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @ApiBody({ type: SizeMatrixFabricCodeDto })
  @Post("/getCombinations")
  async getCombinations(@Body() req?: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getCombinations();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateBomForThread")
  async generateBomForThread(@Body() req?: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateBomForThread(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/generateProposalForHangTag")
  @ApiBody({ type: BomProposalReq })
  async generateProposalForHangTag(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateProposalForHangTag(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/createBomFromForm")
  @ApiBody({ type: BomDto })
  async createBomFromForm(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.createBomFromForm(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/bskOrderForm")
  @ApiBody({ type: BomProposalReq })
  async bskOrderForm(@Body() req: any, @Res() res: Response): Promise<any> {
    try {
      const buffer = await this.bomService.bskOrderForm(req);

      // Set the response headers
      res.setHeader(
        "Content-Type",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
      res.setHeader("Content-Disposition", "attachment; filename=example.xlsx");
      res.setHeader("Content-Length", buffer.length);

      // Send the buffer as the response
      res.send(buffer);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getStylesForSelectedPos")
  async getStylesForSelectedPos(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.getStylesForSelectedPos(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }

  @Post("/getItemsForSelectedPos")
  async getItemsForSelectedPos(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getItemsForSelectedPos(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }

  // @Post('/tagTimeextraction')
  // async tagTimeextraction(): Promise<CommonResponseModel> {
  //     try {
  //         return this.bomService.tagTimeextraction()
  //     } catch (err) {
  //         return this.applicationExceptionHandler.returnException(CommonResponseModel, err)
  //     }
  // }

  @Post("/hTextraction")
  async hTextraction(@Body() req: any, @Res() res: Response): Promise<any> {
    try {
      const buffer = await this.bomService.hTextraction(req);
      res.setHeader(
        "Content-Type",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
      res.setHeader("Content-Disposition", "attachment; filename=example.xlsx");
      res.setHeader("Content-Length", buffer.length);

      res.send(buffer);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  // @Post('/sizeStripEtraction')
  // @ApiBody({ type: BomProposalReq })
  // async sizeStripEtraction(@Body() req: any): Promise<CommonResponseModel> {
  //     try {
  //         return this.bomService.sizeStripEtraction(req)
  //     } catch (err) {
  //         return this.applicationExceptionHandler.returnException(CommonResponseModel, err)
  //     }
  // }

  @Post("/getPPMItemDropdownByCreatedAt")
  async getPPMItemDropdownByCreatedAt(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.trimService.getPPMItemDropdownByCreatedAt(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getPPMStyleByCreatedAt")
  async getPPMStyleByCreatedAt(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.trimService.getPPMStyleByCreatedAt(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }
  @Post("/getPPMSeasonCodeDropdownByCreatedAt")
  async getPPMSeasonCodeDropdownByCreatedAt(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.trimService.getPPMSeasonCodeDropdownByCreatedAt(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getPPMSeasonYearDropdownByCreatedAt")
  async getPPMSeasonYearDropdownByCreatedAt(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.trimService.getPPMSeasonYearDropdownByCreatedAt(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @Post("/getPPMProductCodeDropdownByCreatedAt")
  async getPPMProductCodeDropdownByCreatedAt(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.trimService.getPPMProductCodeDropdownByCreatedAt(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/sizeStripEtraction")
  @ApiBody({ type: BomProposalReq })
  async sizeStripEtraction(
    @Body() req: any,
    @Res() res: Response
  ): Promise<any> {
    try {
      const buffer = await this.bomService.sizeStripEtraction(req);
      res.setHeader(
        "Content-Type",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
      res.setHeader("Content-Disposition", "attachment; filename=example.xlsx");
      res.setHeader("Content-Length", buffer.length);

      // Send the buffer as the response
      res.send(buffer);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @Post("/tagTimeextraction")
  @ApiBody({ type: BomProposalReq })
  async tagTimeextraction(
    @Body() req: any,
    @Res() res: Response
  ): Promise<any> {
    try {
      const buffer = await this.bomService.tagTimeextraction(req);
      res.setHeader(
        "Content-Type",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
      res.setHeader("Content-Disposition", "attachment; filename=example.xlsx");
      res.setHeader("Content-Length", buffer.length);

      // Send the buffer as the response
      res.send(buffer);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  @Post("/mainLabelextraction")
  @ApiBody({ type: BomProposalReq })
  async mainLabelextraction(
    @Body() req: any,
    @Res() res: Response
  ): Promise<any> {
    try {
      const buffer = await this.bomService.mainLabelextraction(req);
      res.setHeader(
        "Content-Type",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
      res.setHeader("Content-Disposition", "attachment; filename=example.xlsx");
      res.setHeader("Content-Length", buffer.length);

      res.send(buffer);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/averyPatchlableextraction")
  @ApiBody({ type: BomProposalReq })
  async averyPatchlableextraction(
    @Body() req: any,
    @Res() res: Response
  ): Promise<any> {
    try {
      const buffer = await this.bomService.averyPatchlableextraction(req);
      res.setHeader(
        "Content-Type",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
      res.setHeader("Content-Disposition", "attachment; filename=example.xlsx");
      res.setHeader("Content-Length", buffer.length);

      res.send(buffer);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getAllBOMs")
  async getAllBOMs(): Promise<CommonResponseModel> {
    try {
      return this.bomService.getAllBOMs();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/deleteBom")
  async deleteBom(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.deleteBom(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/updateOrderFormimMappingBomTable")
  @ApiBody({ type: OrderFormMappingDto })
  async updateOrderFormimMappingBomTable(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.updateOrderFormimMappingBomTable(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post("/getImcodesForMapping")
  async getImcodesForMapping(): Promise<CommonResponseModel> {
    try {
      return this.bomService.getImcodesForMapping();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  @Post('getNotMappedIMsList')
  async getNotMappedIMsList(): Promise<CommonResponseModel> {
      try {
          return this.bomService.getNotMappedIMsList()
      } catch (err) {
          return this.applicationExceptionHandler.returnException(CommonResponseModel, err)
      }
  }

  @Post("/getItemsForNecktapeConsumption")
  async getItemsForNecktapeConsumption(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.getItemsForNecktapeConsumption(req);
    } catch (error) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        error
      );
    }
  }

  @Post("/generateBomForNecktape")
  async generateBomForNecktape(@Body() req: any): Promise<CommonResponseModel> {
    try {
      return this.bomService.generateBomForNecktape(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
  
  @Post("/mapImCodeToItemId")
  @ApiBody({ type: ImCodeItemMapReq })
  async mapImCodeToItemId(
    @Body() req: any
  ): Promise<CommonResponseModel> {
    try {
      return this.bomService.mapImCodeToItemId(req);
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }

  
  @Post("/getAllUnmappedItems")
  async getAllUnmappedItems(): Promise<CommonResponseModel> {
    try {
      return this.bomService.getAllUnmappedItems();
    } catch (err) {
      return this.applicationExceptionHandler.returnException(
        CommonResponseModel,
        err
      );
    }
  }
}
